import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { FaSave, FaTimes, FaArrowLeft } from 'react-icons/fa';
import axios from 'axios';

const ArtikelForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    kategorie: '',
    lieferant: '',
    onlineshoplink: '',
    lagerbestand: 0,
    preis: 0,
    artikelnummer: '',
    einheit: 'Stück', // Standardwert für Einheit
    fuerEtikettendruck: false // Standardwert für Etikettendruck-Checkbox
  });
  
  // Zustand für Feldvalidierung
  const [fieldState, setFieldState] = useState({
    name: { isValid: false, isDuplicate: false },
    kategorie: { isValid: false, isDuplicate: false }
  });
  
  const [validated, setValidated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingArtikel, setLoadingArtikel] = useState(isEditMode);
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  
  // Artikel laden, falls im Bearbeitungsmodus
  useEffect(() => {
    const fetchArtikel = async () => {
      if (isEditMode) {
        try {
          setLoadingArtikel(true);
          setError(null);
          const response = await axios.get(`/api/artikel/${id}`);
          setFormData(response.data);
          
          // Setze Feldstatus für Pflichtfelder nach dem Laden eines existierenden Artikels
          // Dies stellt sicher, dass die Validierungsanzeigen korrekt sind
          if (response.data.name && response.data.name.trim() !== '') {
            setFieldState(prev => ({
              ...prev,
              name: { isValid: true, isDuplicate: false }
            }));
          }
          
          if (response.data.kategorie && response.data.kategorie.trim() !== '') {
            setFieldState(prev => ({
              ...prev,
              kategorie: { isValid: true, isDuplicate: false }
            }));
          }
        } catch (err) {
          console.error('Fehler beim Laden des Artikels:', err);
          setError('Der Artikel konnte nicht geladen werden.');
        } finally {
          setLoadingArtikel(false);
        }
      }
    };

    fetchArtikel();
  }, [id, isEditMode]);
  
  // Input-Änderungen verarbeiten
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let processedValue = value;
    
    // Checkbox-Werte korrekt verarbeiten
    if (type === 'checkbox') {
      processedValue = checked;
    }
    // Zahlen korrekt konvertieren
    else if (type === 'number') {
      processedValue = value === '' ? '' : parseFloat(value);
    }
    
    setFormData(prevData => ({
      ...prevData,
      [name]: processedValue
    }));
    
    // Feldstatus aktualisieren
    if (name === 'name' || name === 'kategorie') {
      setFieldState(prev => ({
        ...prev,
        [name]: { 
          ...prev[name],
          isValid: value.trim() !== '',
          isDuplicate: false // Zurücksetzen des Duplikat-Status bei Änderung
        }
      }));
      
      // Bei Änderung von Name oder Kategorie alle Fehler zurücksetzen
      setError(null);
    }
    
    // Fehler für dieses Feld zurücksetzen
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
      
      // Allgemeinen Fehler zurücksetzen, wenn relevante Felder geändert werden
      if (name === 'name' || name === 'kategorie') {
        setError(null);
      }
    }
  };
  
  // Formular absenden
  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidated(false); // Zurücksetzen des validierten Status
    
    // Manuelle Validierung der Pflichtfelder - gleiche Regeln für Erstellen und Bearbeiten
    const errors = {};
    let isFormValid = true;
    
    // Nur Name und Kategorie sind Pflichtfelder (sowohl bei Neuanlage als auch bei Bearbeitung)
    if (!formData.name.trim()) {
      errors.name = 'Bitte geben Sie einen Namen ein.';
      isFormValid = false;
    }
    
    if (!formData.kategorie.trim()) {
      errors.kategorie = 'Bitte geben Sie eine Kategorie ein.';
      isFormValid = false;
    }
    
    if (!isFormValid) {
      setFormErrors(errors);
      setValidated(true);
      return;
    }
    
    // Alle vorherigen Fehler zurücksetzen
    setFormErrors({});
    setError(null);
    
    // Artikel speichern
    try {
      setLoading(true);
      setError(null);
      
      if (isEditMode) {
        // Benutze die vom Benutzer gewählten Daten ohne Überschreibung des fuerEtikettendruck-Werts
        const updatedData = {
          ...formData
          // fuerEtikettendruck wird nun vom Benutzer-Input übernommen
        };
        
        // Artikel aktualisieren - gleiche Fehlermeldungen wie bei Neuanlage verwenden
        await axios.put(`/api/artikel/${id}`, updatedData);
        navigate(`/artikel/${id}`, { state: { message: 'Artikel wurde erfolgreich aktualisiert.' } });
      } else {
        // Neuen Artikel erstellen
        const response = await axios.post('/api/artikel', formData);
        navigate(`/artikel/${response.data.id}`, { state: { message: 'Artikel wurde erfolgreich erstellt.' } });
      }
    } catch (err) {
      console.error('Fehler beim Speichern des Artikels:', err);
      
      // Einheitliche Fehlerbehandlung für Neuanlage und Bearbeitung
      if (err.response && err.response.data && err.response.data.message) {
        console.log('Backend-Fehlermeldung:', err.response.data.message);
        
        // Prüfe explizit auf die Meldung vom Backend über doppelte Artikel
        if (err.response.data.message.includes('existiert bereits')) {
          // Fall: Artikel mit gleichem Namen in gleicher Kategorie
          if (err.response.data.message.includes('in der Kategorie')) {
            console.log('Duplikat erkannt: Name + Kategorie');
            
            // Spezifische Fehlermeldung für Duplikate von Name+Kategorie setzen
            setFormErrors({
              name: 'Artikelname bereits vergeben in der Kategorie',
              kategorie: 'Artikelname bereits vergeben in der Kategorie'
            });
            
            // Feldstatus für Duplikate setzen
            setFieldState({
              name: { isValid: false, isDuplicate: true },
              kategorie: { isValid: false, isDuplicate: true }
            });
            
            // Spezifische Fehlermeldung statt allgemeiner Fehlermeldung
            setError('Der Artikel konnte nicht gespeichert werden. Artikelname bereits vergeben in der Kategorie.');
          } else {
            // Fehlermeldung für doppelten Namen
            console.log('Duplikat erkannt: Nur Name');
            
            setFormErrors({
              name: 'Ein Artikel mit diesem Namen existiert bereits.'
            });
            
            setFieldState(prev => ({
              ...prev,
              name: { isValid: false, isDuplicate: true }
            }));
            
            setError('Der Artikel konnte nicht gespeichert werden. Ein Artikel mit diesem Namen existiert bereits.');
          }
        } else {
          // Allgemeiner Fehler oder anderer Validierungsfehler
          setError(`Der Artikel konnte nicht gespeichert werden: ${err.response.data.message}`);
        }
      } else {
        // Standardfehler für nicht erkannte Fehlersituationen
        setError('Der Artikel konnte nicht gespeichert werden. Bitte versuchen Sie es später erneut.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Wenn Artikel im Bearbeitungsmodus noch geladen wird
  if (loadingArtikel) {
    return (
      <Container className="text-center my-5">
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Laden...</span>
        </Spinner>
        <p className="mt-2">Artikel wird geladen...</p>
      </Container>
    );
  }

  return (
    <Container>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>{isEditMode ? 'Artikel bearbeiten' : 'Neuen Artikel anlegen'}</h1>
        <Button 
          as={Link} 
          to={isEditMode ? `/artikel/${id}` : '/artikel'} 
          variant="outline-secondary"
          className="d-none d-md-block"
        >
          <FaArrowLeft className="me-2" /> Zurück
        </Button>
      </div>
      
      {error && <Alert variant="danger">{error}</Alert>}
      
      <Card className="shadow-sm mb-4">
        <Card.Body>
          <Form noValidate validated={validated} onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Form.Group as={Col} md="12" controlId="name" className="mb-3">
                <Form.Label 
                  className={formErrors.name || fieldState.name.isDuplicate ? "fw-bold text-danger" : 
                            formData.name.trim() !== '' ? "fw-bold text-success" : "fw-bold"}
                >
                  Artikelname * <small>(Pflichtfeld)</small>
                  {formData.name.trim() !== '' && !formErrors.name && !fieldState.name.isDuplicate && (
                    <span className="ms-2 text-success">✓</span>
                  )}
                  {(formErrors.name || fieldState.name.isDuplicate) && (
                    <span className="ms-2 text-danger">✗</span>
                  )}
                </Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  isInvalid={!!formErrors.name || fieldState.name.isDuplicate}
                  isValid={formData.name.trim() !== '' && !formErrors.name && !fieldState.name.isDuplicate}
                  className={formErrors.name || fieldState.name.isDuplicate ? 'border-danger' : 
                             formData.name.trim() !== '' ? 'border-success' : ''}
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.name || 'Bitte geben Sie einen Namen ein.'}
                </Form.Control.Feedback>
              </Form.Group>
            </Row>
            
            <Row className="mb-3">
              <Form.Group as={Col} md="6" controlId="kategorie" className="mb-3 mb-md-0">
                <Form.Label 
                  className={formErrors.kategorie || fieldState.kategorie.isDuplicate ? "fw-bold text-danger" : 
                            formData.kategorie.trim() !== '' ? "fw-bold text-success" : "fw-bold"}
                >
                  Kategorie * <small>(Pflichtfeld)</small>
                  {formData.kategorie.trim() !== '' && !formErrors.kategorie && !fieldState.kategorie.isDuplicate && (
                    <span className="ms-2 text-success">✓</span>
                  )}
                  {(formErrors.kategorie || fieldState.kategorie.isDuplicate) && (
                    <span className="ms-2 text-danger">✗</span>
                  )}
                </Form.Label>
                <Form.Control
                  type="text"
                  name="kategorie"
                  value={formData.kategorie}
                  onChange={handleChange}
                  placeholder="z.B. Werkzeug, Material, Zubehör"
                  isInvalid={!!formErrors.kategorie || fieldState.kategorie.isDuplicate}
                  isValid={formData.kategorie.trim() !== '' && !formErrors.kategorie && !fieldState.kategorie.isDuplicate}
                  className={formErrors.kategorie || fieldState.kategorie.isDuplicate ? 'border-danger' : 
                             formData.kategorie.trim() !== '' ? 'border-success' : ''}
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.kategorie || 'Bitte geben Sie eine Kategorie ein.'}
                </Form.Control.Feedback>
              </Form.Group>
              
              <Form.Group as={Col} md="6" controlId="lieferant">
                <Form.Label>Lieferant</Form.Label>
                <Form.Control
                  type="text"
                  name="lieferant"
                  value={formData.lieferant}
                  onChange={handleChange}
                  placeholder="z.B. Baumarkt XYZ, Großhandel ABC"
                />
              </Form.Group>
            </Row>
            
            <Row className="mb-3">
              <Form.Group as={Col} md="6" controlId="lagerbestand" className="mb-3 mb-md-0">
                <Form.Label>Lagerbestand</Form.Label>
                <Form.Control
                  type="number"
                  name="lagerbestand"
                  value={formData.lagerbestand}
                  onChange={handleChange}
                  min="0"
                  step="1"
                  isInvalid={!!formErrors.lagerbestand}
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.lagerbestand || 'Der Lagerbestand darf nicht negativ sein.'}
                </Form.Control.Feedback>
              </Form.Group>
              
              <Form.Group as={Col} md="6" controlId="onlineshoplink">
                <Form.Label>Online-Shop Link</Form.Label>
                <Form.Control
                  type="url"
                  name="onlineshoplink"
                  value={formData.onlineshoplink}
                  onChange={handleChange}
                  placeholder="https://example.com/artikel"
                />
              </Form.Group>
            </Row>
            
            <Row className="mb-3">
              <Form.Group as={Col} md="4" controlId="preis" className="mb-3 mb-md-0">
                <Form.Label>Preis (€)</Form.Label>
                <Form.Control
                  type="number"
                  name="preis"
                  value={formData.preis}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  isInvalid={!!formErrors.preis}
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.preis || 'Der Preis darf nicht negativ sein.'}
                </Form.Control.Feedback>
              </Form.Group>
              
              <Form.Group as={Col} md="6" controlId="artikelnummer" className="mb-3 mb-md-0">
                <Form.Label>Artikelnummer <small>(optional)</small></Form.Label>
                <Form.Control
                  type="text"
                  name="artikelnummer"
                  value={formData.artikelnummer}
                  onChange={handleChange}
                  isInvalid={!!formErrors.artikelnummer}
                  placeholder="Optional - kann leer bleiben"
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.artikelnummer}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Optional – kann leer bleiben
                </Form.Text>
              </Form.Group>
              
              <Form.Group as={Col} md="6" controlId="einheit" className="mb-3 mb-md-0">
                <Form.Label>Einheit <small>(Standard: Stück)</small></Form.Label>
                <Form.Select
                  name="einheit"
                  value={formData.einheit}
                  onChange={handleChange}
                  defaultValue="Stück"
                >
                  <option value="Stück">Stück</option>
                  <option value="Meter">Meter</option>
                  <option value="Kilogramm">Kilogramm</option>
                  <option value="Liter">Liter</option>
                  <option value="Packung">Packung</option>
                  <option value="Rolle">Rolle</option>
                  <option value="Satz">Satz</option>
                </Form.Select>
              </Form.Group>
            </Row>
            
            <Row className="mb-3">
              <Form.Group as={Col} controlId="fuerEtikettendruck">
                <Form.Check
                  type="checkbox"
                  label="Zu Etikettendruck hinzufügen"
                  name="fuerEtikettendruck"
                  checked={formData.fuerEtikettendruck}
                  onChange={handleChange}
                />
                <Form.Text className="text-muted">
                  Markierte Artikel werden für den Etikettendruck verfügbar sein.
                </Form.Text>
              </Form.Group>
            </Row>
            
            <div className="d-flex justify-content-end gap-2">
              <Button 
                variant="secondary" 
                as={Link} 
                to={isEditMode ? `/artikel/${id}` : '/artikel'}
                disabled={loading}
              >
                <FaTimes className="me-2" /> Abbrechen
              </Button>
              <Button 
                type="submit" 
                variant="primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                    Speichern...
                  </>
                ) : (
                  <>
                    <FaSave className="me-2" /> Speichern
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
      
      <Button 
        as={Link} 
        to={isEditMode ? `/artikel/${id}` : '/artikel'} 
        variant="outline-secondary"
        className="d-block d-md-none mb-4"
      >
        <FaArrowLeft className="me-2" /> Zurück
      </Button>
    </Container>
  );
};

export default ArtikelForm;
